// client/extensions/Comment/Comment.ts
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { XSnackbar } from "@adornis/chemistry/elements/components/x-snackbar.js";
import { currentUser } from "@adornis/users/client/currentUser.js";
import { Extension, Mark, mergeAttributes } from "@tiptap/core";
import { Subject, takeUntil } from "rxjs";
var CommentTranslations = {
  comment: {
    tooltip: { addComment: ["Kommentar hinzuf\xFCgen", "Add Comment"] },
    scrollIntoView: ["In den Blick scrollen", "Scroll into View"],
    edit: ["Bearbeiten", "Edit"],
    delete: ["L\xF6schen", "Delete"],
    reply: ["Kommentar kommentieren", "Reply to Comment"]
  }
};
var COMMENT_EXTENSION_NAME = "comments";
function createCommentExtension(options) {
  const commandPipe = new Subject();
  const commentsUpdate = new Subject();
  const comments = {};
  const state = {
    userID: null,
    username: null,
    commandPipe,
    commentsUpdate: commentsUpdate.asObservable(),
    comments,
    sendCommand: (command) => {
      commandPipe.next(command);
    }
  };
  const internalState = {
    userID: "",
    username: "",
    commentsUpdate,
    destroySubject: new Subject()
  };
  if (options?.userProvider) {
    options.userProvider.pipe(takeUntil(internalState.destroySubject)).subscribe((user) => {
      if (typeof user === "object") {
        internalState.username = state.username = user.username;
        internalState.userID = state.userID = user.userID;
      } else {
        internalState.username = state.username = user;
        internalState.userID = state.userID = null;
      }
    });
  } else {
    currentUser().pipe(takeUntil(internalState.destroySubject)).subscribe((user) => {
      internalState.userID = state.userID = user?._id;
      internalState.username = state.username = user?.username;
    });
  }
  return {
    extension: {
      styles: ({ host }) => [
        {
          "x-comment": {
            position: "relative",
            borderBottom: "2px solid #115a9780"
          },
          ".x-comment-selected": {
            borderBottom: "2px solid #115a97"
          },
          // :hover > .x-comment-icon,
          ".x-comment-selected > .x-comment-icon": {
            opacity: 1
          },
          ".x-comment-icon": {
            display: "inline",
            opacity: 0.5,
            position: "absolute",
            top: "-16px",
            left: "0",
            fontSize: "12px",
            padding: "0 2px",
            userSelect: "none",
            wordBreak: "normal"
          }
        }
      ],
      extensions: [CommentTipTapExtension(options, state, internalState)],
      buttons: ({ editor, host }) => [
        {
          name: "addComment",
          icon: "add_comment",
          tooltip: host.translator.translate("tiptap", "comment", "tooltip", "addComment"),
          action: () => {
            if (options.addCommentCallback) {
              options.addCommentCallback();
            }
            editor.chain().addComment().run();
          }
        }
      ]
    },
    state
  };
}
function dateParser(k, v) {
  if (k === "createdAt" || k === "updatedAt") {
    return new Date(v);
  }
  return v;
}
function generateReplyID(comment) {
  const randomString = Math.random().toString(36).substring(2, 7);
  return `${comment.id}_${randomString}`;
}
function extractReplyID(id) {
  const parts = id.split("_");
  if (parts.length < 2) {
    return null;
  }
  return { commentID: parts[0], replyID: parts[1] };
}
function extractComments(editor) {
  const comments = {};
  editor.state.doc.descendants((node, pos) => {
    for (const c of node.marks.filter((m) => m.type.name === "comment")) {
      try {
        const commentData = JSON.parse(c.attrs["comment-data"], dateParser);
        comments[commentData.id] = commentData;
      } catch (e) {
      }
    }
    return true;
  });
  return comments;
}
function findCommentMarkbyID(editor, id) {
  let res = null;
  editor.state.doc.descendants((node, pos) => {
    for (const c of node.marks.filter((m) => m.type.name === "comment")) {
      if (c.attrs["comment-id"] !== id) {
        continue;
      }
      res = { mark: c, node, pos };
      return false;
    }
    return true;
  });
  return res;
}
function buildPathToReply(id) {
  const ids = id.split("_");
  const paths = ids.map((v, i) => {
    return {
      id: ids.slice(0, i + 1).join("_"),
      type: v.length > 5 ? "comment" : "reply"
    };
  });
  return paths;
}
function findCommentMarkbyReplyID(editor, id) {
  let res = null;
  if (!extractReplyID(id)) {
    logger.warn("Could not find comment mark by reply id " + id + ". Its not a reply id");
    return res;
  }
  const path = buildPathToReply(id);
  if (!path || path.length === 0)
    return res;
  editor.state.doc.descendants((node, pos) => {
    for (const c of node.marks.filter((m) => m.type.name === "comment")) {
      if (c.attrs["comment-id"] !== path[0].id) {
        continue;
      }
      res = { mark: c, node, pos };
      return false;
    }
    return true;
  });
  return res;
}
function updateCommentMarkByID(editor, tr, id, fn, force) {
  const rootId = id.indexOf("_") !== -1 ? id.split("_")[0] : id;
  logger.info({ id }, "updating comment mark");
  const res = findCommentMarkbyID(editor, id) ?? findCommentMarkbyReplyID(editor, id);
  if (!res) {
    logger.warn("Could not find comment mark by id " + id);
    return;
  }
  try {
    const commentData = JSON.parse(res.mark.attrs["comment-data"], dateParser);
    fn(commentData);
    tr.addMark(
      res.pos,
      res.pos + res.node.nodeSize,
      editor.state.doc.type.schema.mark("comment", {
        "comment-id": rootId,
        "comment-data": JSON.stringify(commentData),
        /**
         * ! Hacky way to force a re-render of the comment mark.
         * TODO: find a better solution to force a re-render.
         */
        "comment-hc": force ? (Math.random() * 999).toFixed(0) : void 0
      })
    );
  } catch (e) {
  }
}
function CommentTipTapExtension(options, state, internalState) {
  let highligtedID = "";
  return Extension.create({
    name: COMMENT_EXTENSION_NAME,
    priority: 999,
    onCreate() {
      state.commandPipe.asObservable().pipe(takeUntil(internalState.destroySubject)).subscribe((command) => {
        switch (command.type) {
          case "AddComment":
            this.editor.chain().addComment();
            break;
          case "DeleteComment":
            if (!command.id) {
              return;
            }
            this.editor.chain().deleteComment(command.id).run();
            break;
          case "EditComment":
            if (!command.id || !command.content) {
              return;
            }
            this.editor.chain().editComment(command.id, command.content).run();
            break;
          case "AddReply":
            if (!command.id || !command.userID || !command.username) {
              return;
            }
            console.log(command);
            this.editor.chain().addReply(command.userID, command.username, command.id).run();
            break;
          case "Highlight":
            this.editor.chain().highlightComment(command.id).run();
            break;
          case "ScrollTo":
            if (!command.id) {
              return;
            }
            this.editor.chain().scrollToComment(command.id).run();
            break;
        }
      });
      state.comments = extractComments(this.editor);
      internalState.commentsUpdate.next(true);
    },
    onUpdate() {
      state.comments = extractComments(this.editor);
      internalState.commentsUpdate.next(true);
    },
    onDestroy() {
      internalState.destroySubject.next(true);
      internalState.destroySubject.complete();
      internalState.commentsUpdate.complete();
    },
    addExtensions: () => [
      Mark.create({
        name: "comment",
        inclusive: false,
        addAttributes: () => ({
          "comment-data": {
            type: "string",
            default: () => ""
          },
          "comment-id": {
            type: "string",
            default: () => ""
          },
          "comment-hc": {
            type: "string",
            default: () => ""
          }
        }),
        parseHTML: () => [{ tag: "x-comment" }],
        renderHTML({ HTMLAttributes }) {
          const iconWrapper = (args, els) => args === null ? els : [
            [
              "x-comment-icon",
              { class: "x-comment-icon", contenteditable: "false" },
              [
                "div",
                {
                  style: `width: 24px; height: 24px; background-image: url("data:image/svg+xml,%3Csvg class='w-6 h-6 text-gray-800 dark:text-white' aria-hidden='true' xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' viewBox='0 0 24 24'%3E%3Cpath stroke='%23115a97' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M7 9h5m3 0h2M7 12h2m3 0h5M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-6.616a1 1 0 0 0-.67.257l-2.88 2.592A.5.5 0 0 1 8 18.477V17a1 1 0 0 0-1-1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z'/%3E%3C/svg%3E%0A");`
                }
              ]
            ],
            ["span", ...els]
          ];
          return [
            "x-comment",
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
              class: highligtedID === HTMLAttributes["comment-id"] ? "x-comment-selected" : ""
            }),
            ...iconWrapper(HTMLAttributes, [0])
          ];
        }
      })
    ],
    addCommands: () => ({
      addComment: () => (param) => {
        if (!internalState.userID || !internalState.username) {
          return false;
        }
        const from = param.editor.state.selection.from;
        const to = param.editor.state.selection.to;
        if (from === to) {
          return false;
        }
        const newID = A.getGloballyUniqueID();
        const nodeFrom = param.editor.state.doc.nodeAt(from);
        const nodeTo = param.editor.state.doc.nodeAt(to - 1);
        if (!nodeFrom || !nodeTo) {
          return false;
        }
        if (nodeFrom !== nodeTo) {
          XSnackbar.show(`Comments can't span multiple nodes!`);
          return false;
        }
        param.state.tr.addMark(
          from,
          to,
          param.state.doc.type.schema.mark("comment", {
            "comment-id": newID,
            "comment-data": JSON.stringify({
              id: newID,
              userID: internalState.userID,
              username: internalState.username,
              content: "",
              replies: [],
              createdAt: /* @__PURE__ */ new Date()
            })
          })
        );
        return false;
      },
      deleteComment: (id) => (param) => {
        const res = findCommentMarkbyID(param.editor, id);
        if (res) {
          param.state.tr.removeMark(res.pos, res.pos + res.node.nodeSize, res.mark);
        } else {
          const path = buildPathToReply(id);
          if (path.length === 0) {
            return false;
          }
          const deleteReply = (path2, root) => {
            if (path2.length === 1) {
              root.replies = root.replies.filter((reply) => reply.id !== id);
            } else {
              const reply = path2.splice(0, 1)[0];
              const comment = root.replies.find((c) => c.id === reply.id);
              if (!comment) {
                return;
              }
              return deleteReply(path2, comment);
            }
          };
          updateCommentMarkByID(param.editor, param.tr, path[0].id, (comment) => {
            return deleteReply(path.slice(1), comment);
          });
        }
        return false;
      },
      editComment: (id, content) => (param) => {
        let editFn;
        if (id.indexOf("_") !== -1) {
          const path = buildPathToReply(id).slice(1);
          const editReply = (path2, root) => {
            if (path2.length === 0) {
              root.content = content;
              return;
            } else {
              const reply = path2.splice(0, 1)[0];
              const comment = root.replies.find((c) => c.id === reply.id);
              if (!comment) {
                logger.warn("Could not find comment to edit", id, reply.id);
                return;
              }
              return editReply(path2, comment);
            }
          };
          editFn = (comment) => editReply(path, comment);
        } else {
          editFn = (comment) => {
            comment.content = content;
          };
        }
        updateCommentMarkByID(param.editor, param.tr, id, editFn);
        return false;
      },
      highlightComment: (id) => (param) => {
        updateCommentMarkByID(param.editor, param.tr, id ?? highligtedID ?? "", (comment) => {
        }, true);
        highligtedID = id;
        return false;
      },
      scrollToComment: (id) => (param) => {
        const res = findCommentMarkbyID(param.editor, id) ?? findCommentMarkbyReplyID(param.editor, id);
        if (res) {
          param.editor.commands.setTextSelection({ from: res.pos, to: res.pos + res.node.nodeSize });
          param.editor.commands.scrollIntoView();
        }
        return false;
      },
      addReply: (userID, username, id) => (param) => {
        if (!internalState.userID || !internalState.username) {
          return false;
        }
        updateCommentMarkByID(param.editor, param.tr, id, (comment) => {
          const path = buildPathToReply(id).slice(1);
          if (!path)
            return;
          const pushReply = (path2, root) => {
            if (path2.length === 0) {
              root.replies.push({
                id: generateReplyID(root),
                userID: internalState.userID ?? "",
                username: internalState.username ?? "",
                content: "",
                createdAt: /* @__PURE__ */ new Date(),
                replies: []
              });
            } else {
              const reply = path2.splice(0, 1)[0];
              const comment2 = root.replies.find((c) => c.id === reply.id);
              if (!comment2) {
                return;
              }
              return pushReply(path2, comment2);
            }
          };
          return pushReply(path, comment);
        });
        return false;
      }
    })
  });
}
export {
  CommentTranslations,
  createCommentExtension
};
